import React, { useMemo, useState } from "react";
import { formatInr, toNumber } from "../utils/format";

/* ─────────────────────────────────────────────────────────────────────
   PRECISION TAX CALCULATION LOGIC (REVISED FY 2026-27 / 2027-28)
───────────────────────────────────────────────────────────────────── */

function calculateTax(inputs, regime, fy = "2026-27") {
  const isFY27 = fy === "2026-27";
  
  // 1. Tax-Free Allowances Breakdown
  const childCount = toNumber(inputs.childrenCount);
  const eduExempt = Math.min(toNumber(inputs.childEduMonthly), 3000) * childCount * 12;
  const hostelExempt = Math.min(toNumber(inputs.hostelMonthly), 9000) * childCount * 12;
  const mealExempt = Math.min(toNumber(inputs.mealMonthly), 8800) * 12;
  const totalAllowances = eduExempt + hostelExempt + mealExempt + toNumber(inputs.sec10);

  // 2. HRA Details for Audit
  let hraAudit = null;
  let hraExemption = 0;
  if (regime === "old") {
    const basic = toNumber(inputs.hraBasic) * 12;
    const hraRec = toNumber(inputs.hraReceived) * 12;
    const rentPaid = toNumber(inputs.hraRent) * 12;
    if (basic > 0) {
      const a1 = hraRec;
      const a2 = Math.max(0, rentPaid - (0.1 * basic));
      const a3 = (inputs.isMetro ? 0.5 : 0.4) * basic;
      hraExemption = Math.min(a1, a2, a3);
      hraAudit = { a1, a2, a3, isMetro: inputs.isMetro, final: hraExemption, basic, rent: rentPaid };
    }
  }

  // 3. Deductions Breakdown
  const deds = [];
  if (regime === "old") {
    deds.push({ label: "Std. Ded", val: 50000 });
    const s80cTotal = Math.min(toNumber(inputs.sec80c) + toNumber(inputs.epfContribution), 150000);
    if (s80cTotal > 0) deds.push({ label: "80C (Inc EPF)", val: s80cTotal });
    if (toNumber(inputs.sec80d)) deds.push({ label: "80D Self", val: toNumber(inputs.sec80d) });
    if (toNumber(inputs.sec80dParents)) deds.push({ label: "80D Parents", val: toNumber(inputs.sec80dParents) });
    const s24 = Math.min(toNumber(inputs.homeLoanInterest), 200000); if (s24) deds.push({ label: "S24 Int", val: s24 });
    const nps = Math.min(toNumber(inputs.nps), 50000); if (nps) deds.push({ label: "NPS 1B", val: nps });
    const tta = Math.min(toNumber(inputs.sec80tta), 10000); if (tta) deds.push({ label: "80TTA", val: tta });
    if (toNumber(inputs.sec80e)) deds.push({ label: "80E Loan", val: toNumber(inputs.sec80e) });
    if (toNumber(inputs.donation)) deds.push({ label: "80G Donat.", val: toNumber(inputs.donation) });
    if (toNumber(inputs.profTax)) deds.push({ label: "Prof. Tax", val: toNumber(inputs.profTax) });
    if (hraExemption > 0) deds.push({ label: "HRA Exemption", val: hraExemption });
  } else {
    deds.push({ label: "Std. Ded", val: isFY27 ? 75000 : 50000 });
  }

  const totalDeductions = deds.reduce((a, b) => a + b.val, 0);
  const dedDescription = deds.map(d => `${d.label}:${(d.val/1000).toFixed(0)}k`).join("+");

  const incomeTotal = toNumber(inputs.income) + toNumber(inputs.interestIncome) + toNumber(inputs.otherIncome);
  const adjustedGross = Math.max(0, incomeTotal - totalAllowances);
  const taxable = Math.max(adjustedGross - totalDeductions, 0);
  
  let tax = 0;
  const slabs = [];
  const slabCalc = (limit, rate, prev) => {
    if (taxable <= prev) return;
    const amt = Math.min(taxable - prev, limit);
    const t = amt * rate; tax += t;
    if (t > 0 || taxable > prev) slabs.push({ label: `₹${(prev/100000).toFixed(1)}L-₹${((prev+limit)/100000).toFixed(1)}L`, rate: `${Math.round(rate*100)}%`, tax: t });
  };

  if (regime === "old") {
    let ex = (inputs.age === "senior" ? 300000 : inputs.age === "super" ? 500000 : 250000);
    slabCalc(500000-ex, 0.05, ex); slabCalc(500000, 0.20, 500000); if (taxable > 1000000) slabCalc(10000000, 0.30, 1000000);
  } else {
    if (isFY27) {
       slabCalc(400000, 0.05, 400000); slabCalc(400000, 0.10, 800000); slabCalc(400000, 0.15, 1200000); slabCalc(400000, 0.20, 1600000); slabCalc(400000, 0.25, 2000000); if (taxable > 2400000) slabCalc(10000000, 0.30, 2400000);
    } else {
       slabCalc(300000, 0.05, 300000); slabCalc(300000, 0.10, 600000); slabCalc(300000, 0.15, 900000); slabCalc(300000, 0.20, 1200000); if (taxable > 1500000) slabCalc(10000000, 0.30, 1500000);
    }
  }

  const threshold = (regime === "old" ? 500000 : (isFY27 ? 1200000 : 700000));
  const rebate87A = taxable <= threshold ? tax : 0;
  const taxAfterRebate = Math.max(0, tax - rebate87A);
  const totalTax = taxAfterRebate * 1.04;
  
  return { 
    gross: incomeTotal, adjustedGross, allowances: totalAllowances, deductions: totalDeductions, dedDescription, taxable, 
    taxBeforeRebate: tax, rebate87A, cess: taxAfterRebate * 0.04, totalTax, tds: toNumber(inputs.tds), payable: totalTax - toNumber(inputs.tds), slabs,
    audit: { eduExempt, hostelExempt, mealExempt, hraAudit }
  };
}

/* ── COMPACT PROFESSIONAL PRINT REPORT ── */
function MeticulousPrintReport({ oldR, newR, fy, inputs }) {
  const formatDate = () => {
    const d = new Date();
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hr = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const sec = String(d.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hr}:${min}:${sec}`;
  };

  const auditData = oldR.audit;
  const hra = auditData.hraAudit;

  return (
    <div className="tax-print-summary" id="tax-report" style={{ background: '#fff', padding: '10mm', color: '#1e293b', fontSize: '8.5px' }}>
       <div className="print-header" style={{ textAlign: 'center', marginBottom: '2mm', fontWeight: 'bold' }}>calculateyourtax.in</div>
       
       <div style={{ maxWidth: '780px', margin: '0 auto', border: '1px solid #1e293b', padding: '4mm' }}>
          <header style={{ textAlign: 'center', marginBottom: '4mm', borderBottom: '2px solid #1e293b', paddingBottom: '2mm' }}>
             <h1 style={{ fontSize: '16px', margin: 0, color: '#1e293b' }}>TAX COMPUTATION AUDIT REPORT</h1>
             <p style={{ margin: '2px 0' }}>AY 2027-28 | FY 20{fy} | Taxpayer Age: {inputs.age.toUpperCase()}</p>
          </header>

          {/* SECTION I: INCOME & ALLOWANCES */}
          <section style={{ marginBottom: '4mm', pageBreakInside: 'avoid' }}>
             <h2 style={{ fontSize: '9px', background: '#f1f5f9', padding: '4px 8px', margin: '0 0 4px 0', borderLeft: '3px solid #1e293b' }}>I. INCOME & ALLOWANCE BREAKDOWN</h2>
             <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '3mm' }}>
                <tbody>
                   <tr style={{ background: '#fcfcfc' }}>
                      <td style={{ padding: '4px', border: '1px solid #e2e8f0' }}>Gross Annual Salary</td>
                      <td style={{ padding: '4px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 'bold' }}>{formatInr(toNumber(inputs.income))}</td>
                      <td style={{ padding: '4px', border: '1px solid #e2e8f0' }}>Children Education Exemption</td>
                      <td style={{ padding: '4px', border: '1px solid #e2e8f0', textAlign: 'right' }}>{formatInr(auditData.eduExempt)}</td>
                   </tr>
                   <tr>
                      <td style={{ padding: '4px', border: '1px solid #e2e8f0' }}>Interest & Other Sources</td>
                      <td style={{ padding: '4px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 'bold' }}>{formatInr(toNumber(inputs.interestIncome) + toNumber(inputs.otherIncome))}</td>
                      <td style={{ padding: '4px', border: '1px solid #e2e8f0' }}>Hostel Expenditure Exemption</td>
                      <td style={{ padding: '4px', border: '1px solid #e2e8f0', textAlign: 'right' }}>{formatInr(auditData.hostelExempt)}</td>
                   </tr>
                   <tr>
                      <td style={{ padding: '4px', border: '1px solid #e2e8f0' }}><strong>GROSS TOTAL INCOME</strong></td>
                      <td style={{ padding: '4px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 'bold' }}>{formatInr(oldR.gross)}</td>
                      <td style={{ padding: '4px', border: '1px solid #e2e8f0' }}>Meal Voucher Exemption</td>
                      <td style={{ padding: '4px', border: '1px solid #e2e8f0', textAlign: 'right' }}>{formatInr(auditData.mealExempt)}</td>
                   </tr>
                </tbody>
             </table>
          </section>

          {/* SECTION II: HRA AUDIT */}
          {hra && (
            <section style={{ marginBottom: '4mm', pageBreakInside: 'avoid' }}>
               <h2 style={{ fontSize: '9px', background: '#f1f5f9', padding: '4px 8px', margin: '0 0 4px 0', borderLeft: '3px solid #1e293b' }}>II. HRA EXEMPTION COMPUTATION (OLD REGIME)</h2>
               <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px', marginBottom: '2mm' }}>
                  <div style={{ border: '1px solid #e2e8f0', padding: '4px', textAlign: 'right' }}>
                    <div style={{ fontSize: '7px', color: '#64748b', textAlign: 'left' }}>1. Actual HRA Received</div>
                    <strong>{formatInr(hra.a1)}</strong>
                  </div>
                  <div style={{ border: '1px solid #e2e8f0', padding: '4px', textAlign: 'right' }}>
                    <div style={{ fontSize: '7px', color: '#64748b', textAlign: 'left' }}>2. Rent Paid (-) 10% Basic</div>
                    <strong>{formatInr(hra.a2)}</strong>
                  </div>
                  <div style={{ border: '1px solid #e2e8f0', padding: '4px', textAlign: 'right', background: '#f8fafc' }}>
                    <div style={{ fontSize: '7px', color: '#64748b', textAlign: 'left' }}>3. {hra.isMetro ? '50%' : '40%'} Basic Salary</div>
                    <strong>{formatInr(hra.a3)}</strong>
                  </div>
                  <div style={{ border: '1px solid #1e293b', padding: '4px', textAlign: 'right', background: '#eff6ff' }}>
                    <div style={{ fontSize: '7px', color: '#1e40af', fontWeight: 'bold', textAlign: 'left' }}>CLAIMABLE HRA (Least of 1,2,3)</div>
                    <strong>{formatInr(hra.final)}</strong>
                  </div>
               </div>
               <div style={{ fontSize: '7px', position: 'relative', top: '-2px', color: '#64748b' }}>*Metro cities (50% Basic) applies for Delhi, Mumbai, Kolkata, Chennai, Bengaluru, Hyderabad, Pune, Ahmedabad.</div>
            </section>
          )}

          {/* SECTION III: DEDUCTIONS */}
          <section style={{ marginBottom: '4mm', pageBreakInside: 'avoid' }}>
             <h2 style={{ fontSize: '9px', background: '#f1f5f9', padding: '4px 8px', margin: '0 0 4px 0', borderLeft: '3px solid #1e293b' }}>III. ITEM-WISE DEDUCTIONS SUMMARY</h2>
             <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '3px' }}>
                <div style={{ border: '1px solid #e2e8f0', padding: '3px', textAlign: 'right' }}><span style={{ fontSize: '7px', display: 'block', textAlign: 'left' }}>Sec 80C (Inc EPF)</span><strong>{formatInr(Math.min(toNumber(inputs.sec80c)+toNumber(inputs.epfContribution), 150000))}</strong></div>
                <div style={{ border: '1px solid #e2e8f0', padding: '3px', textAlign: 'right' }}><span style={{ fontSize: '7px', display: 'block', textAlign: 'left' }}>Sec 80D (Self)</span><strong>{formatInr(toNumber(inputs.sec80d))}</strong></div>
                <div style={{ border: '1px solid #e2e8f0', padding: '3px', textAlign: 'right' }}><span style={{ fontSize: '7px', display: 'block', textAlign: 'left' }}>Sec 80D (Parents)</span><strong>{formatInr(toNumber(inputs.sec80dParents))}</strong></div>
                <div style={{ border: '1px solid #e2e8f0', padding: '3px', textAlign: 'right' }}><span style={{ fontSize: '7px', display: 'block', textAlign: 'left' }}>NPS 80CCD(1B)</span><strong>{formatInr(Math.min(toNumber(inputs.nps), 50000))}</strong></div>
                <div style={{ border: '1px solid #e2e8f0', padding: '3px', textAlign: 'right' }}><span style={{ fontSize: '7px', display: 'block', textAlign: 'left' }}>Home Loan S24</span><strong>{formatInr(Math.min(toNumber(inputs.homeLoanInterest), 200000))}</strong></div>
                <div style={{ border: '1px solid #e2e8f0', padding: '3px', textAlign: 'right' }}><span style={{ fontSize: '7px', display: 'block', textAlign: 'left' }}>Savings 80TTA</span><strong>{formatInr(Math.min(toNumber(inputs.sec80tta), 10000))}</strong></div>
                <div style={{ border: '1px solid #e2e8f0', padding: '3px', textAlign: 'right' }}><span style={{ fontSize: '7px', display: 'block', textAlign: 'left' }}>Annual EPF</span><strong>{formatInr(toNumber(inputs.epfContribution))}</strong></div>
                <div style={{ border: '1px solid #e2e8f0', padding: '3px', textAlign: 'right' }}><span style={{ fontSize: '7px', display: 'block', textAlign: 'left' }}>Already Paid TDS</span><strong>{formatInr(toNumber(inputs.tds))}</strong></div>
                <div style={{ border: '1px solid #e2e8f0', padding: '3px', textAlign: 'right' }}><span style={{ fontSize: '7px', display: 'block', textAlign: 'left' }}>Prof. Tax / Other</span><strong>{formatInr(toNumber(inputs.profTax) + toNumber(inputs.sec10))}</strong></div>
                <div style={{ border: '1px solid #10b981', background: '#f0fdf4', padding: '3px', textAlign: 'right' }}><span style={{ fontSize: '7px', display: 'block', textAlign: 'left', fontWeight: 'bold' }}>TOTAL DEDUCTS</span><strong>{formatInr(oldR.deductions)}</strong></div>
             </div>
          </section>

          {/* SECTION IV: COMPARISON */}
          <section style={{ marginBottom: '4mm', pageBreakInside: 'avoid' }}>
             <h2 style={{ fontSize: '9px', background: '#f1f5f9', padding: '4px 8px', margin: '0 0 4px 0', borderLeft: '3px solid #1e293b' }}>IV. REGIME COMPUTATION COMPARISON</h2>
             <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5mm' }}>
                {[oldR, newR].map((res, i) => (
                   <div key={i} style={{ border: '1px solid #cbd5e1', padding: '3mm' }}>
                      <div style={{ textAlign: 'center', background: '#1e293b', color: '#fff', padding: '2px', fontWeight: 'bold', marginBottom: '2mm', fontSize: '9px' }}>{i === 0 ? 'OLD REGIME' : 'NEW REGIME'}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Gross Total Income</span><span style={{ fontWeight: 'bold' }}>{formatInr(res.gross)}</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#10b981' }}><span>Exempt Allowances</span><span>-{formatInr(res.allowances)}</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#ef4444' }}><span>Total Deductions</span><span>-{formatInr(res.deductions)}</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderTop: '1px solid #e2e8f0', fontWeight: 'bold', marginTop: '1px' }}><span>NET TAXABLE</span><span>{formatInr(res.taxable)}</span></div>
                      <div style={{ margin: '2px 0', background: '#f8fafc', padding: '2px', fontSize: '7px' }}>
                         {res.slabs.map((s, idx) => <div key={idx} style={{ display: 'flex', justifyContent: 'space-between' }}><span>{s.label} ({s.rate})</span><span>{formatInr(s.tax)}</span></div>)}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Tax Subtotal</span><span>{formatInr(res.taxBeforeRebate)}</span></div>
                      {res.rebate87A > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', color: '#10b981' }}><span>Sec 87A Rebate</span><span>-{formatInr(res.rebate87A)}</span></div>}
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Cess (4%)</span><span>{formatInr(res.cess)}</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e2e8f0', fontWeight: 'bold' }}><span>Total Tax Liability</span><span>{formatInr(res.totalTax)}</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', background: '#f1f5f9' }}><span>Less: TDS Paid</span><span>-{formatInr(res.tds)}</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: res.payable < 0 ? '#10b981' : '#ef4444', fontWeight: 'bold', fontSize: '10px', marginTop: '1mm' }}>
                         <span>{res.payable < 0 ? 'REFUND DUE' : 'NET TAX DUE'}</span><span>{formatInr(Math.abs(res.payable))}</span>
                      </div>
                   </div>
                ))}
             </div>
          </section>

          <footer style={{ marginTop: '2mm', textAlign: 'center', borderTop: '1px solid #e2e8f0', paddingTop: '2mm' }}>
             <div style={{ background: '#f0fdf4', padding: '3px 8px', border: '1px solid #10b981', display: 'inline-block', marginBottom: '1mm' }}>
                <strong style={{ fontSize: '9px' }}>OPTIMAL CHOICE: {oldR.totalTax <= newR.totalTax ? 'OLD REGIME' : 'NEW REGIME'} (Net Savings: {formatInr(Math.abs(oldR.totalTax - newR.totalTax))})</strong>
             </div>
             <div style={{ fontSize: '7px', color: '#64748b' }}>Precision Tax Computation Audit | Generated on {formatDate()}</div>
          </footer>
       </div>
       <div className="print-footer" style={{ marginTop: '3mm', textAlign: 'center', fontWeight: 'bold' }}>calculateyourtax.in</div>
    </div>
  );
}

const DEFAULT = {
  income: "", interestIncome: "", otherIncome: "", tds: "",
  sec80c: "", sec80d: "", sec80dParents: "", epfContribution: "", 
  nps: "", sec80tta: "", sec80e: "", donation: "", homeLoanInterest: "", 
  age: "general", profTax: "", sec10: "",
  hraBasic: "", hraReceived: "", hraRent: "", isMetro: false,
  childEduMonthly: "", hostelMonthly: "", mealMonthly: "", childrenCount: "0"
};

export default function TaxCalculator() {
  const [step, setStep] = useState(1);
  const [fy, setFy] = useState("2026-27");
  const [inputs, setInputs] = useState(DEFAULT);
  const set = (k) => (e) => setInputs(p => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const oldR = useMemo(() => calculateTax(inputs, "old", fy), [inputs, fy]);
  const newR = useMemo(() => calculateTax(inputs, "new", fy), [inputs, fy]);
  const best = oldR.totalTax <= newR.totalTax ? "old" : "new";

  return (
    <>
      <MeticulousPrintReport oldR={oldR} newR={newR} fy={fy} inputs={inputs} />
      <div className="calculator">
        <div className="calc-steps">
           <div className={step === 1 ? "calc-step active" : "calc-step"} onClick={() => setStep(1)}>1. Income & Allowances</div>
           <div className={step === 2 ? "calc-step active" : "calc-step"} onClick={() => setStep(2)}>2. HRA & Deductions</div>
           <div className={step === 3 ? "calc-step active" : "calc-step"} onClick={() => setStep(3)}>3. Final Result</div>
        </div>
        <div className="calculator-body">
           {step === 1 && (
             <div className="form-grid">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                   <label className="field"><span>Tax Year</span><select value={fy} onChange={(e) => setFy(e.target.value)}><option value="2025-26">FY 2025-26</option><option value="2026-27">FY 2026-27</option></select></label>
                   <label className="field"><span>Assessee Age</span><select value={inputs.age} onChange={set("age")}><option value="general">Below 60</option><option value="senior">60-80</option><option value="super">80+</option></select></label>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                   <label className="field"><span>Annual Salary</span><input type="number" placeholder="0" value={inputs.income} onChange={set("income")} /></label>
                   <label className="field"><span>Already Paid TDS</span><input type="number" placeholder="0" value={inputs.tds} onChange={set("tds")} /></label>
                </div>

                <div style={{ border: '1px solid #e2e8f0', padding: '15px', borderRadius: '10px', background: '#f8fafc' }}>
                   <div style={{ fontWeight: 800, fontSize: '13px', marginBottom: '12px', color: '#1e293b' }}>Tax-Free Allowances (2026 Limits)</div>
                   <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <label className="field"><span>Children Edu (Monthly/Child)</span><input type="number" placeholder="Limit ₹3,000" value={inputs.childEduMonthly} onChange={set("childEduMonthly")} /></label>
                      <label className="field"><span>Hostel Exp (Monthly/Child)</span><input type="number" placeholder="Limit ₹9,000" value={inputs.hostelMonthly} onChange={set("hostelMonthly")} /></label>
                      <label className="field"><span>Meal Vouchers (Monthly)</span><input type="number" placeholder="Limit ₹8,800" value={inputs.mealMonthly} onChange={set("mealMonthly")} /></label>
                      <label className="field"><span>Number of Children</span><select value={inputs.childrenCount} onChange={set("childrenCount")}><option value="0">0 Children</option><option value="1">1 Child</option><option value="2">2 Children</option></select></label>
                   </div>
                </div>
                <button className="btn primary" onClick={() => setStep(2)}>Next Step →</button>
             </div>
           )}
           {step === 2 && (
             <div className="form-grid">
                <div style={{ border: '1px solid #e2e8f0', padding: '15px', borderRadius: '10px', background: '#eff6ff' }}>
                   <div style={{ fontWeight: 800, fontSize: '13px', marginBottom: '12px', color: '#2563eb' }}>HRA Details (For Old Regime)</div>
                   <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <label className="field"><span>Monthly Basic Salary</span><input type="number" value={inputs.hraBasic} onChange={set("hraBasic")} /></label>
                      <label className="field"><span>Monthly HRA Received</span><input type="number" value={inputs.hraReceived} onChange={set("hraReceived")} /></label>
                      <label className="field"><span>Monthly Rent Paid</span><input type="number" value={inputs.hraRent} onChange={set("hraRent")} /></label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', fontWeight: 700 }}>
                         <input type="checkbox" checked={inputs.isMetro} onChange={set("isMetro")} /> Live in Metro City (50% Cap)
                      </label>
                   </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                   <label className="field"><span>Section 80C</span><input type="number" value={inputs.sec80c} onChange={set("sec80c")} /></label>
                   <label className="field"><span>EPF (Annual)</span><input type="number" value={inputs.epfContribution} onChange={set("epfContribution")} /></label>
                   <label className="field"><span>80D (Self)</span><input type="number" value={inputs.sec80d} onChange={set("sec80d")} /></label>
                   <label className="field"><span>80D (Parents)</span><input type="number" value={inputs.sec80dParents} onChange={set("sec80dParents")} /></label>
                   <label className="field"><span>Home Loan Int (S24)</span><input type="number" value={inputs.homeLoanInterest} onChange={set("homeLoanInterest")} /></label>
                   <label className="field"><span>NPS 80CCD(1B)</span><input type="number" value={inputs.nps} onChange={set("nps")} /></label>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                   <button className="btn" onClick={() => setStep(1)}>Back</button>
                   <button className="btn primary" onClick={() => setStep(3)} style={{ flex: 1 }}>Compare Regimes →</button>
                </div>
             </div>
           )}
           {step === 3 && (
             <div className="results-area">
                <div className="results-grid">
                   {[oldR, newR].map((res, i) => (
                      <div key={i} className={((i===0 && best==='old') || (i===1 && best==='new')) ? "card card-best" : "card"}>
                         <div style={{ padding: '15px', textAlign: 'center', background: '#1e293b', color: '#fff', fontWeight: 800 }}>{i===0 ? 'OLD REGIME' : 'NEW REGIME'}</div>
                         <div style={{ padding: '24px', textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>{res.payable < 0 ? 'REFUND DUE' : 'NET TAX PAYABLE'}</div>
                            <div style={{ fontSize: '26px', fontWeight: 900, color: res.payable < 0 ? '#10b981' : '#ef4444' }}>{formatInr(Math.abs(res.payable))}</div>
                            <div style={{ marginTop: '10px', fontSize: '11px', color: '#94a3b8' }}>Total Liability: {formatInr(res.totalTax)}</div>
                         </div>
                      </div>
                   ))}
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '30px' }}>
                   <button className="btn primary" onClick={() => window.print()}>Download Audit Report</button>
                   <button className="btn" onClick={() => { setInputs(DEFAULT); setStep(1); }}>Reset</button>
                </div>
             </div>
           )}
        </div>
      </div>
    </>
  );
}
